import { useState, useEffect } from 'react';
import { Save, Loader2, ChevronDown, ChevronUp, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { welcomeApi } from '../../api/welcome';
import { WelcomeMarkdownRenderer } from '../WelcomePage/WelcomeMarkdownRenderer';
import type { WelcomePage } from '../../types';

export function WelcomeEditor() {
  const navigate = useNavigate();
  const [welcomePage, setWelcomePage] = useState<WelcomePage | null>(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    loadWelcomePage();
  }, []);

  const loadWelcomePage = async () => {
    setLoading(true);
    setError(null);

    try {
      const page = await welcomeApi.getCurrent();
      setWelcomePage(page);
      setContent(page.content);
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to load welcome page');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!content.trim()) {
      setError('Content cannot be empty');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updated = await welcomeApi.update({ content });
      setWelcomePage(updated);
      setSuccess(`Welcome page updated successfully! Version is now ${updated.version}.`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to save welcome page');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-monster-dark">
      {/* Header */}
      <div className="bg-monster-card border-b border-monster-border">
        <div className="max-w-screen-2xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="w-10 h-10 flex items-center justify-center rounded hover:bg-monster-dark transition-colors"
                title="Back to Admin"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-2xl font-bold">Welcome Page Editor</h1>
                {welcomePage && (
                  <p className="text-sm text-gray-400">
                    Current version: {welcomePage.version} • Last updated: {new Date(welcomePage.updatedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowHelp(!showHelp)}
                className="btn-secondary text-sm"
              >
                {showHelp ? <ChevronUp size={16} className="inline mr-1" /> : <ChevronDown size={16} className="inline mr-1" />}
                Syntax Help
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !content.trim()}
                className="btn-primary"
              >
                {saving ? (
                  <>
                    <Loader2 className="inline animate-spin mr-2" size={16} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="inline mr-2" size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Help Panel (Collapsible) */}
      {showHelp && (
        <div className="bg-monster-blue/10 border-b border-monster-blue/30">
          <div className="max-w-screen-2xl mx-auto px-6 py-4">
            <h3 className="font-semibold mb-3">Markdown Syntax Guide</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400 mb-1">Headings:</p>
                <code className="block bg-monster-dark p-2 rounded mb-2">
                  # Heading 1<br />
                  ## Heading 2<br />
                  ### Heading 3
                </code>
              </div>
              <div>
                <p className="text-gray-400 mb-1">Text Formatting:</p>
                <code className="block bg-monster-dark p-2 rounded mb-2">
                  **Bold text**<br />
                  *Italic text*
                </code>
              </div>
              <div>
                <p className="text-gray-400 mb-1">Lists:</p>
                <code className="block bg-monster-dark p-2 rounded mb-2">
                  - Bullet item<br />
                  * Another bullet<br />
                  1. Numbered item
                </code>
              </div>
              <div>
                <p className="text-gray-400 mb-1">Embeds (must be on own line):</p>
                <code className="block bg-monster-dark p-2 rounded mb-2">
                  [audio:123]<br />
                  [image:456]
                </code>
                <p className="text-xs text-gray-500 mt-1">
                  Replace 123/456 with actual song IDs from your library
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="max-w-screen-2xl mx-auto px-6 pt-4">
          <div className="p-3 bg-monster-crimson/10 border border-monster-crimson/30 rounded flex items-start gap-2">
            <AlertCircle className="text-monster-crimson flex-shrink-0 mt-0.5" size={18} />
            <p className="text-sm text-gray-300">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-screen-2xl mx-auto px-6 pt-4">
          <div className="p-3 bg-green-500/10 border border-green-500/30 rounded flex items-start gap-2">
            <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
            <p className="text-sm text-gray-300">{success}</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-12 h-12 animate-spin text-monster-blue" />
        </div>
      ) : (
        <div className="max-w-screen-2xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[calc(100vh-280px)]">

            {/* Editor Panel */}
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold mb-3 text-monster-blue">Editor</h3>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="flex-1 w-full px-4 py-3 bg-monster-card border border-monster-border rounded-lg focus:outline-none focus:border-monster-blue font-mono text-sm resize-none"
                placeholder="Enter your welcome page content here using markdown..."
                spellCheck={false}
              />
              <p className="text-xs text-gray-500 mt-2">
                {content.length} characters • Saving will increment version to {(welcomePage?.version || 0) + 1}
              </p>
            </div>

            {/* Preview Panel */}
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold mb-3 text-monster-blue">Live Preview</h3>
              <div className="flex-1 overflow-y-auto px-4 py-3 bg-monster-card border border-monster-border rounded-lg">
                {content.trim() ? (
                  <WelcomeMarkdownRenderer content={content} />
                ) : (
                  <p className="text-gray-500 text-center py-10">
                    Preview will appear here as you type...
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
